import React, { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import db from "../firebase-config.ts";
import { useUser } from "./UserProvider.tsx";
import { useNavigate } from 'react-router-dom'; 

interface StudentData {
  name: string;
  matricNumber: number;
  program: string;
  year: string;
  email: string;
  phone: string;
  address: string;
  district: string;
  assistanceType: string;
  faculty: string;
  campus: string;
  mahallah: string;
  recipient: string;
  averageIncome: string;
  session: string;
  enrollmentDate: string;
  expectedGraduation: string;
}

const StudentProfile: React.FC = () => {
  const { user, isAuthenticated } = useUser();
   const navigate = useNavigate();
  const [studentData, setStudentData] = useState<StudentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState<string | null>(null);


  useEffect(() => {
        if (!isAuthenticated) {
          navigate('/login'); // Redirect to login page
        }
      }, [isAuthenticated, navigate]);
  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser?.email) {
        setEmail(currentUser.email);
      } else {
        console.error("User is not authenticated or does not have an email.");
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchStudentData = async () => {
      if (email) {
        try {
          const noMatrik = parseInt(email.split("@")[0], 10);

          const studentsCollection = collection(db, "students");
          const q = query(studentsCollection, where("no_matrik", "==", noMatrik));
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            const data = doc.data();

            setStudentData({
              name: data.nama_penuh || "Unknown",
              matricNumber: noMatrik,
              program: data.jurusan || "Unknown",
              year: data.tahun_pengajian || "Unknown",
              email: `${noMatrik}@ikas.com`,
              phone: data.no_telefon || "Unknown",
              address: data.alamat_tempat_tinggal || "Unknown",
              district: data.daerah || "Unknown",
              assistanceType: data.jenis_bantuan || "Unknown",
              faculty: data.kuliah || "Unknown",
              campus: data.kampus || "Unknown",
              mahallah: data.mahallah || "Unknown",
              recipient: data.penerima_bantuan || "Unknown",
              averageIncome: data.purata_pendapatan || "Unknown",
              session: data.sesi_kemasukan_ke_uia || "Unknown",
              enrollmentDate: data.tahun_pengajian || "Unknown",
              expectedGraduation: data.dijangka_tamat_pengajian || "Unknown",
            });
          } else {
            console.error("No student data found for this matric number.");
          }
        } catch (error) {
          console.error("Error fetching student data:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchStudentData();
  }, [email]);

  if (loading) {
    return <p className="text-gray-200 text-center">Loading...</p>;
  }

  if (!studentData) {
    return <p className="text-gray-200 text-center">No data available for this user.</p>;
  }

  return isAuthenticated ? (
    <div className="min-h-screen text-gray-200 p-6">
      {/* Header */}
      <header className="mb-6">
        <h1 className="text-4xl font-bold">Student Profile Dashboard</h1>
        <p className="text-gray-100">Manage your academic and personal information.</p>
      </header>

      {/* Main Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Overview */}
        <div className="bg-gray-800 p-6 rounded-lg shadow col-span-1">
          <div className="flex flex-col items-center text-center">
            <img
              src={`https://smartcard.iium.edu.my/packages/card/printing/camera/uploads/original/${studentData.matricNumber}.jpeg`}
              alt={`${studentData.name}'s Profile`}
              className="w-32 h-32 rounded-full shadow-lg mb-4"
              onError={(e) => {
                e.currentTarget.src = "https://via.placeholder.com/150"; // Fallback image
              }}
            />
            <h2 className="text-xl font-bold">{studentData.name}</h2>
            <p className="text-gray-400"> {studentData.matricNumber}</p>
            <p className="text-gray-400"> {studentData.program}</p>
            <p className="text-gray-400">{studentData.year}</p>
          </div>
        </div>

        {/* Personal and Academic Details */}
        <div className="bg-gray-800 p-6 rounded-lg shadow col-span-2">
          <h2 className="text-xl font-bold mb-4">Details</h2>
          <div className="grid grid-cols-2 gap-4">
            <p className="text-sm">
              <strong>Email:</strong> {studentData.email}
            </p>
            <p className="text-sm">
              <strong>Phone:</strong> {studentData.phone}
            </p>
            <p className="text-sm">
              <strong>Address:</strong> {studentData.address}
            </p>
            <p className="text-sm">
              <strong>District:</strong> {studentData.district}
            </p>
            <p className="text-sm">
              <strong>Assistance Type:</strong> {studentData.assistanceType}
            </p>
            <p className="text-sm">
              <strong>Faculty:</strong> {studentData.faculty}
            </p>
            <p className="text-sm">
              <strong>Campus:</strong> {studentData.campus}
            </p>
            <p className="text-sm">
              <strong>Mahallah:</strong> {studentData.mahallah}
            </p>
            <p className="text-sm">
              <strong>Aid Recipient:</strong> {studentData.recipient}
            </p>
            <p className="text-sm">
              <strong>Average Income:</strong> {studentData.averageIncome}
            </p>
            <p className="text-sm">
              <strong>Session:</strong> {studentData.session}
            </p>
            <p className="text-sm">
              <strong>Year Of Study:</strong> {studentData.enrollmentDate}
            </p>
            
          </div>
        </div>

        {/* QR Code Section */}
        <div className="bg-gray-800 p-6 rounded-lg shadow col-span-1 lg:col-span-3">
          <h2 className="text-xl font-bold mb-4">Profile QR Code</h2>
          <div className="flex justify-center">
            <QRCodeCanvas
              value={studentData.name}
              size={150}
              bgColor="#1f2937"
              fgColor="#ffffff"
              className="shadow-lg"
            />
          </div>
          <p className="text-center text-sm text-gray-400 mt-2">
            Scan the QR code to view your profile online.
          </p>
        </div>
      </div>
    </div>
  ) : null; 
};

export default StudentProfile;
